/* eslint-disable camelcase */
import React, { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';

import { useDeleteLiveClassByPkMutation } from '@school_ify/schoolify-repository/src/teacher/types';

import { getUser } from 'redux/authentication/auth.slice';

import LessonsItem from './components/LessonsItem';
import Notification from '../../../views/LiveClass/components/Notifications/Modal';
import Button from '../../Button/Button';
import UseLiveClassDetails from '../../../hooks/useLiveClassDetails';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { getHeaderClasses } from '../../../redux/header/selectors';
import {
  teacherCreateLiveClassState,
  teacherGetMyLiveClasses,
} from '../../../redux/liveClass/getLiveClassSlice.slice';
import {
  studentCreateLiveClassState,
  studentGetMyLiveClasses,
} from '../../../redux/liveClass/studentLiveClassesList.slice';

import './LiveClassLessonsList.scoped.scss';

const LiveClassLessonsList: React.FC = () => {
  const [activeClasses, setActiveClasses] = useState([]);
  const [inactiveClasses, setInActiveClasses] = useState([]);
  const [upcomingClasses, setUpcomingClasses] = useState([]);
  const user = useAppSelector(getUser);
  const { response, loading } = useAppSelector(teacherCreateLiveClassState);
  const { responseStudents, loadingStudents } = useAppSelector(studentCreateLiveClassState);

  const [deleteLiveClassByPk] = useDeleteLiveClassByPkMutation();

  const filter = data => {
    const inActiveClassesFiltered = [];
    const activeClassesFiltered = [];
    const upcomingClassesFiltered = [];
    for (let i = 0; i < data.length; i++) {
      const obj = data[i];
      const ends = DateTime.fromISO(obj.starting_at).plus({ minutes: obj.duration });
      let starts = DateTime.fromISO(obj.starting_at);
      const now = DateTime.now();

      if (user.role === 'teacher') {
        starts = DateTime.fromISO(obj.starting_at).minus({ minutes: 15 });
      }
      if (ends < now) {
        inActiveClassesFiltered.push({
          ...obj,
          ...{
            status: 'Finished',
          },
        });
      }

      if (obj.status === 'Ended') {
        inActiveClassesFiltered.push({
          ...obj,
          ...{
            status: 'Finished',
          },
        });
        // eslint-disable-next-line no-continue
        continue;
      }

      if (ends > now && starts < now) {
        activeClassesFiltered.push({
          ...obj,
          ...{
            status: {
              en: 'In Progress',
              de: 'im Gange',
            },
          },
        });
      }
      if (starts > now) {
        upcomingClassesFiltered.push({
          ...obj,
          ...{
            status: {
              en: 'Upcoming',
              de: 'Demnächst',
            },
          },
        });
      }
    }

    setActiveClasses(activeClassesFiltered);
    setInActiveClasses(inActiveClassesFiltered);
    setUpcomingClasses(upcomingClassesFiltered);
  };

  const dispatch = useAppDispatch();
  const classes = useAppSelector(getHeaderClasses);

  const { t } = useTranslation('live_classes');
  const { endVideo } = UseLiveClassDetails();

  useEffect(() => {
    if (loading === 'success') {
      filter(response);
    }
  }, [response]);

  useEffect(() => {
    if (loadingStudents === 'success') {
      filter(responseStudents);
    }
  }, [responseStudents]);

  useEffect(() => {
    switch (user.role) {
      case 'teacher':
        if (!classes) {
          return;
        }
        dispatch(
          teacherGetMyLiveClasses(
            classes.map(obj => {
              return obj.id;
            })
          )
        );
        break;
      case 'student':
        dispatch(studentGetMyLiveClasses(user.id));
        break;
      default: // do nothing
        break;
    }
  }, [classes]);

  const [openModal, setOpenModal] = useState(false);
  const [isUpcomingSelected, setUpcomingSelected] = useState(false);
  const [classId, setClassId] = useState('');

  const finishClass = id => {
    setClassId(id);
    setUpcomingSelected(false);
    setOpenModal(prevState => !prevState);
  };
  const handleUpcomingClassModal = id => {
    setClassId(id);
    setUpcomingSelected(true);
    setOpenModal(prevState => !prevState);
  };
  const deleteClass = async id => {
    setInActiveClasses(prev => [...prev.filter(item => item.id !== id)]);
    await deleteLiveClassByPk({ variables: { id } });
  };
  const deleteUpcomingClass = async () => {
    setOpenModal(false);
    setUpcomingClasses(prev => [...prev.filter(item => item.id !== classId)]);
    await deleteLiveClassByPk({ variables: { id: classId } });
  };
  const handleModal = () => {
    setOpenModal(prevState => !prevState);
  };
  const closeClass = async () => {
    await endVideo(classId);
    const list = JSON.parse(JSON.stringify(activeClasses));
    const index = list.findIndex(element => {
      return element.id === classId;
    });
    setInActiveClasses([...inactiveClasses, list[index]]);
    list.splice(list.indexOf(index), 1);
    setActiveClasses(list);
    handleModal();
  };

  return (
    <div>
      {loading === 'success' || loadingStudents === 'success' ? (
        <>
          <Notification handleModal={handleModal} openModal={openModal}>
            <h3>{t('Are you sure?')}</h3>
            <p>{t('You are about to delete class.')}</p>
            <div className="flex gap-10px justify-between m-y-3">
              <Button
                style={{
                  background: '#5CD79E',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: 600,
                  padding: '0.5em 2em',
                }}
                onClick={isUpcomingSelected ? deleteUpcomingClass : closeClass}
              >
                Yes
              </Button>
              <Button
                style={{
                  background: '#F05B40',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: 600,
                  padding: '0.5em 2em',
                }}
                onClick={handleModal}
              >
                No
              </Button>
            </div>
          </Notification>
          <LessonsItem
            key={JSON.stringify(upcomingClasses) + Math.random()}
            listOfClasses={upcomingClasses}
            main_class="upcoming"
            finishClass={handleUpcomingClassModal}
          />
          <LessonsItem
            listOfClasses={activeClasses}
            main_class="warning"
            key={JSON.stringify(activeClasses)}
            finishClass={finishClass}
          />
          <LessonsItem
            key={JSON.stringify(inactiveClasses) + Math.random()}
            listOfClasses={inactiveClasses}
            main_class="danger"
            finishClass={deleteClass}
          />
        </>
      ) : (
        <div>{t('List is empty')}</div>
      )}
    </div>
  );
};

export default LiveClassLessonsList;
